import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { FormattedMessage } from "react-intl";
import { useSelector, useDispatch } from "react-redux";

import { Switcher } from "../../switcher/Switcher";
import UserMenu from "./userMenu/UserMenu";
import ButtonLink from "../../buttonLink/ButtonLink";
import {
  Desktop,
  Mobile,
  NotMobile,
  TabletMobile,
} from "../../../hocs/Responsive";
import { MobileMenuContainer } from "../mobileMenu/MobileMenuContainer";
import { PortalTitle } from "../portalTitle/PortalTitle";
import {
  getSBEnabledStatus,
  updateWebFilter,
  getBusinessName,
} from "../../../store/account";
import { useLogoutActionWithPrompt } from "../../../hocs/useLogoutActionWithPrompt";
import { getLogoLink, getCustomLinkRef } from "../../../store/config.selectors";
import { getSubscriberName } from "../../../store/info/info.selectors";
import { logout } from "../../../pages/login/login.actions";
import { HelpLink } from "../../helpLink/HelpLink";
import { Icon } from "../../icon/Icon";

import "./CommonHeader.scss";

const UserInfo = () => {
  const dispatch = useDispatch();
  const customLinkRef = useSelector(getCustomLinkRef);
  const portalName = useSelector(getBusinessName);
  const isSbEnabled = useSelector(getSBEnabledStatus);
  const subscriberName = useSelector(getSubscriberName);

  const handleLogout = useLogoutActionWithPrompt();

  const handleUpdateWebFilter = (e) => {
    dispatch(updateWebFilter(e.target.checked));
  };

  return (
    <div className="header__info">
      {customLinkRef && (
        <a className={"custom-header-link"} href={customLinkRef}>
          <FormattedMessage id={"custom_header_link"} />
        </a>
      )}
      <Desktop>
        <Switcher
          id="web-filter-switch"
          checked={isSbEnabled}
          onChange={handleUpdateWebFilter}
          label={<FormattedMessage id="protection" />}
        />
        <HelpLink className="header__link" hasIcon={false}>
          <Icon icon={"fa-regular fa-question-circle"} />
          <span>
            <FormattedMessage id={"help"} />
          </span>
        </HelpLink>
        <UserMenu
          subscriberName={subscriberName}
          logout={handleLogout}
          portalName={portalName}
        />
      </Desktop>
      <TabletMobile>
        <MobileMenuContainer
          subscriberName={subscriberName}
          portalName={portalName}
          logout={handleLogout}
          isSbEnabled={isSbEnabled}
          onChange={handleUpdateWebFilter}
        />
      </TabletMobile>
    </div>
  );
};

const LogOutLink = () => {
  const dispatch = useDispatch();

  return (
    <ButtonLink className="header__link" onClick={() => dispatch(logout())}>
      <FormattedMessage id={"logout"} />
    </ButtonLink>
  );
};

const HeaderHelpLink = () => {
  return (
    <HelpLink className="header__link">
      <NotMobile>
        <FormattedMessage id={"help_and_documentation"} />
      </NotMobile>
      <Mobile>
        <FormattedMessage id={"help"} />
      </Mobile>
    </HelpLink>
  );
};

export const CommonHeader = ({
  showUserInfo,
  showHeaderUnderline,
  showLogOutLink,
  showHelpLink,
}) => {
  const portalName = useSelector(getBusinessName);
  const logoLink = useSelector(getLogoLink);

  return (
    <div
      className={classnames("header", {
        "header--bordered": showHeaderUnderline,
      })}
    >
      <div className={"header__content layout__container"}>
        <PortalTitle title={portalName} link={logoLink} />
        {showUserInfo && <UserInfo />}
        {showHelpLink && <HeaderHelpLink />}
        {showLogOutLink && <LogOutLink />}
      </div>
    </div>
  );
};

CommonHeader.propTypes = {
  showUserInfo: PropTypes.bool,
  showHeaderUnderline: PropTypes.bool,
  showHelpLink: PropTypes.bool,
  showLogOutLink: PropTypes.bool,
};
